import { useEffect, useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCatalogFlowPreview } from "@/context/cms-puck/CatalogFlowPreviewContext";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase } from "@/lib/cms/cms-new-phrases";
import type { SelectionItem } from "@/lib/cms-puck/blocks/selection";
import {
  fetchCatalogItemsByIds,
  fetchCatalogList,
  type CatalogPickItem,
} from "@/api/cms-puck/catalog-picker-api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  closePuckDrawer,
  openPuckDrawer,
} from "@/components/cms-puck/fields/puck-drawer-store";
import { ItemSpotlightPickerBody } from "@/components/cms-puck/fields/ItemSpotlightPickerField";

function Thumb({
  url,
  size = 28,
}: {
  url?: string;
  size?: number;
}) {
  if (!url) {
    return (
      <div
        className="shrink-0 rounded bg-slate-100"
        style={{ width: size, height: size }}
        aria-hidden
      />
    );
  }
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={url}
      alt=""
      width={size}
      height={size}
      className="shrink-0 rounded bg-slate-100 object-cover"
      style={{ width: size, height: size }}
    />
  );
}

function itemLabel(item: CatalogPickItem, fallback: string): string {
  return (
    [item.registrationNumber, item.title].filter(Boolean).join(" — ") ||
    fallback
  );
}

function toSelectionItem(
  pick: CatalogPickItem,
  type: "artwork" | "multipleprint",
): SelectionItem {
  return { type, id: pick.id };
}

/**
 * Sélecteur d'œuvre/multiple d'aperçu (éditeur) pour `CatalogueItemDetail`.
 * Parité fiche rubrique : défaut = 1er élément publié de la cible du parcours
 * (œuvres ou multiples) ; sur le site, l'URL prime.
 */
export function CatalogueItemPreviewField({
  value,
  onChange,
}: {
  value: SelectionItem | null | undefined;
  onChange: (next: SelectionItem | null) => void;
}) {
  const cmsNew = useCmsNew();
  const { target, isLoading: flowLoading } = useCatalogFlowPreview();
  const itemType: "artwork" | "multipleprint" =
    target === "MULTIPRINTS" ? "multipleprint" : "artwork";
  const allowedTypes = useMemo((): Array<"artwork" | "multipleprint"> => {
    if (target === "MULTIPRINTS") return ["multipleprint"];
    if (target === "ARTWORKS") return ["artwork"];
    return ["artwork", "multipleprint"];
  }, [target]);

  const listQuery = useQuery({
    queryKey: ["catalog-item-preview-options", itemType],
    queryFn: () => fetchCatalogList(itemType, ""),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const options = listQuery.data ?? [];

  // Ancrer un défaut dès que la liste est prête (comme la fiche rubrique).
  useEffect(() => {
    if (options.length === 0) return;
    const current =
      value?.id &&
      (value.type === "artwork" || value.type === "multipleprint") &&
      allowedTypes.includes(value.type)
        ? value
        : null;
    const stillValid =
      current && options.some((o) => o.id === current.id);
    if (stillValid) return;
    const first = options[0];
    if (!first) return;
    const next = toSelectionItem(first, itemType);
    if (current?.id === next.id && current?.type === next.type) return;
    onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync défaut une fois options prêtes
  }, [options.length, itemType, target]);

  const selectedId =
    value?.id && options.some((o) => o.id === value.id)
      ? value.id
      : options[0]?.id;

  const { data: resolved = [] } = useQuery({
    queryKey: ["catalog-item-preview-selected", itemType, selectedId],
    queryFn: () =>
      selectedId
        ? fetchCatalogItemsByIds(itemType, [selectedId])
        : Promise.resolve([]),
    enabled: !!selectedId,
  });
  const selectedChip = resolved[0] ?? options.find((o) => o.id === selectedId);

  const openFullPicker = () => {
    openPuckDrawer({
      title: cmsNew(
        "cms.new.auto.title.choisir_un_element",
        "Choisir un élément",
      ),
      maxWidth: "sm:max-w-2xl",
      node: (
        <ItemSpotlightPickerBody
          selected={value?.id ? value : undefined}
          allowedTypes={[...allowedTypes]}
          onPick={(next) => {
            onChange(next);
            closePuckDrawer();
          }}
        />
      ),
    });
  };

  const loading = flowLoading || listQuery.isLoading;
  if (loading) {
    return (
      <p className="p-1 text-xs text-slate-400">
        {cmsNew("cms.new.auto.text.chargement", "Chargement…")}
      </p>
    );
  }

  if (options.length === 0) {
    return (
      <p className="p-1 text-xs text-slate-500">
        {cmsNew(
          "cms.new.hint.catalogueItemPreview.empty",
          target === "MULTIPRINTS"
            ? "Aucun multiple publié trouvé pour l'aperçu."
            : "Aucune œuvre publiée trouvée pour l'aperçu.",
        )}
      </p>
    );
  }

  const fallbackLabel = cmsNew(
    "cms.new.auto.text.element",
    "Élément",
  );

  return (
    <div className="space-y-1.5 p-1">
      <p className="text-xs text-slate-500">
        {cmsNew(
          "cms.new.hint.catalogueItemPreview",
          "Aperçu éditeur : choisissez l'œuvre ou le multiple simulé. Sur le site, le contenu vient de l'URL.",
        )}
      </p>
      <Select
        value={selectedId}
        onValueChange={(id) => {
          const pick = options.find((o) => o.id === id);
          if (pick) onChange(toSelectionItem(pick, itemType));
        }}
      >
        <SelectTrigger className="h-10 w-full bg-white">
          <SelectValue
            placeholder={cmsNew(
              "cms.new.pickers.catalogue.chooseItem",
              "Choisir un élément",
            )}
          >
            {selectedChip ? (
              <span className="flex min-w-0 items-center gap-2">
                <Thumb url={selectedChip.url} />
                <span className="truncate">
                  {itemLabel(selectedChip, fallbackLabel)}
                </span>
              </span>
            ) : null}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {options.slice(0, 40).map((o) => (
            <SelectItem key={o.id} value={o.id} className="py-1.5">
              <span className="flex min-w-0 items-center gap-2">
                <Thumb url={o.url} />
                <span className="truncate">
                  {itemLabel(o, fallbackLabel)}
                </span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full"
        onClick={openFullPicker}
      >
        {cmsPhrase(cmsNew, "changeElement")}
      </Button>
    </div>
  );
}
